/* Where Entra returns to after sign-in - and it is a bounce, not the finish.
 *
 * This route exists because the redirect URI has to be a real path the SPA can
 * serve - nginx rewrites unmatched paths to index.html, so the router sees it.
 *
 * **THE TOKEN EXCHANGE DOES NOT HAPPEN HERE.** MSAL's `navigateToLoginRequestUrl`
 * defaults to true, so `handleRedirectPromise()` on this route caches the
 * response hash and navigates back to whichever page started sign-in - normally
 * `/`, where SignIn.tsx calls `ready()` again and *that* call completes the
 * exchange. MSAL says so itself, in @azure/msal-browser@5.20.0 at
 * dist/interaction_client/RedirectClient.mjs:177 for the default and :224 for
 * the reasoning: "Default behavior is to redirect to the start page and not
 * process the hash now. The start page is expected to also call
 * handleRedirectPromise."
 *
 * Which makes this the wrong place to look for a broken sign-in, and it is
 * written down because that is exactly where the search starts. A failure whose
 * symptom is "the callback flashed and nothing happened" is almost always on the
 * start page or in Entra, not in this file.
 *
 * The `failed` branch below therefore catches a failure of the caching step
 * only. It is not the error path for a rejected authorization code.
 *
 * It is not a screen anyone should linger on, so there is no branding and no
 * spinner ceremony. If it is visible for more than an instant, something is
 * wrong and the message says so.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { account, ready } from "../auth";
import s from "./AuthCallback.module.css";

export default function AuthCallback() {
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    ready()
      .then(() => {
        if (cancelled) return;
        // Signed in or not, this route is a waypoint. Sending an unauthenticated
        // caller back to sign-in rather than into the app keeps the guard in one
        // place instead of two.
        navigate(account() ? "/app" : "/", { replace: true });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <main className={s["body"]}>
      {failed ? (
        <p className="notice" role="status">
          Sign-in did not complete. Return to the start page and try again.
        </p>
      ) : (
        <p className="mono">Completing sign-in…</p>
      )}
    </main>
  );
}
